/**
 * Aufbereitung von Job-Fehlern fuer die Oberflaeche.
 *
 * Zwei Faelle, die bisher gleich aussahen und es nicht sind:
 *
 * 1. `awaitJob` laeuft in sein Timeout. Der Job ist dann NICHT gescheitert, er laeuft weiter —
 *    der Worker wiederholt fehlgeschlagene Jobs bis zu dreimal, das kann laenger dauern als das
 *    Frontend wartet. „Fehlgeschlagen" waere hier schlicht falsch.
 * 2. Der Job meldet `failed`. Dann steht in `error` der Rohtext des Worker-Fehlers, bei
 *    Vario-Importen mehrere hundert Zeichen Diagnose. Ungekuerzt ist das in einem Toast
 *    unlesbar; die verwertbare Aussage steckt meist in einem kleinen Teil davon.
 */

/** Erkennungsmuster der Timeout-Meldung aus `awaitJob`. */
const TIMEOUT_RE = /did not finish within/i;

/**
 * Kern einer Vario-data-import-Fehlermeldung. Aufbau:
 * `data-import ERRONEOUS import=… run=…: <messageCode> | <Klartext> || entries: [ … ]`
 * Interessant ist der Klartext zwischen `| ` und `||`.
 */
const VARIO_IMPORT_RE = /\|\s*([^|]+?)\s*\|\|/;

export interface JobFailureText {
  /** Kurze Meldung fuer den Toast. */
  message: string;
  /** true, wenn der Job weiterlaeuft und nur die Wartezeit abgelaufen ist. */
  stillRunning: boolean;
}

/**
 * Baut die Nutzermeldung aus einem Fehler, den `awaitJob` geworfen hat oder der aus
 * `job.error` stammt. `subject` benennt die Aktion, z. B. „Übernahme".
 */
export function describeJobFailure(err: unknown, subject: string): JobFailureText {
  const raw = err instanceof Error ? err.message : String(err ?? '');

  if (TIMEOUT_RE.test(raw)) {
    return {
      stillRunning: true,
      message: `${subject} dauert länger als erwartet und läuft im Hintergrund weiter. Die Liste zeigt das Ergebnis, sobald der Job durch ist.`,
    };
  }

  const varioCore = raw.match(VARIO_IMPORT_RE)?.[1]?.trim();
  const detail = varioCore && varioCore.length > 0 ? varioCore : raw.trim();
  return {
    stillRunning: false,
    message: `${subject} fehlgeschlagen: ${truncate(detail, 200)}`,
  };
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}
